import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { auth } from '@/api/client';
import { errorText } from '@/lib/permissionError';

/**
 * F2 — Self-service authenticator reset modal.
 *
 * User must provide their current password AND a valid TOTP code from
 * their current enrolment. On success, the modal instructs them to sign
 * out and back in; the next login lands on the MFA enrolment flow.
 *
 * Users who have LOST both their authenticator AND recovery codes cannot
 * self-reset — the copy in this dialog directs them to their Site Admin.
 */
export function ResetAuthenticatorDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
}) {
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setPassword('');
    setTotp('');
    setError(null);
    setSubmitting(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!password) {
      setError('Please enter your current password.');
      return;
    }
    if (!/^\d{6}$/.test(totp)) {
      setError('Enter the 6-digit code from your current authenticator app.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await auth.resetMfaSelf(password, totp);
      toast.success(res.message || 'Authenticator reset.');
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(errorText(err, 'Could not reset authenticator.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
      title="Reset authenticator"
      description="Use this to move your authenticator app to a new device. You will re-enrol on your next sign-in."
      width={500}
    >
      <div className="mb-4 flex items-start gap-2 rounded-sm border border-warning/40 bg-warning-soft px-3 py-2 text-[11.5px] text-warning-strong">
        <ShieldAlert size={14} className="mt-0.5 shrink-0" />
        <span>
          If you have <strong>lost</strong> your current authenticator app and can&apos;t enter a code,
          contact your Site Admin — they can reset MFA from the admin console.
        </span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <label className="block">
          <span className="block text-[11.5px] font-semibold text-text-2 mb-1">Current password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            maxLength={200}
            className="w-full h-10 px-3 rounded-sm border border-border-strong text-sm focus:outline-none focus:ring-2 focus:ring-navy"
          />
        </label>

        <label className="block">
          <span className="block text-[11.5px] font-semibold text-text-2 mb-1">6-digit code from your current authenticator</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={totp}
            onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            className="w-full h-10 px-3 rounded-sm border border-border-strong text-lg tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-navy"
          />
        </label>

        {error && (
          <div className="text-[11.5px] text-critical bg-critical-soft border border-critical/30 rounded-sm px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-9 px-3 rounded-sm text-[12.5px] text-text-2 hover:bg-soft"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="h-9 px-4 rounded-sm bg-navy text-white text-[12.5px] font-bold flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? 'Resetting' : 'Reset authenticator'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
